import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { useGetIndividualsQuery, useSearchIndividualMutation } from 'api/IndividualsApiSlice';
import {
  CheckboxFilter,
  DateRangeFilter,
  filterByDateRange,
  FilterChip,
  filterByValues,
  getFilterOptions,
  sortTableData,
  TableComponent,
} from 'components';
import { format } from 'date-fns';
import { useDebounce } from 'use-debounce';
import { Status } from 'utils';
import {
  emptyIndividualFilters,
  getIndividualColumns,
  IndividualFilters,
  individualFilterValues,
  IndividualSort,
  individualSortValues,
} from 'utils/constants';
import { TableHeader } from './components/TableHeader';
import { RootState } from 'app-redux/store';

const AUTH_TOKEN_WAIT_MS = 5000;

export type IndividualTableData = {
  firstName: ReactNode;
  lastName: ReactNode;
  dateOfBirth: ReactNode;
  status?: Status;
  assignedTo: ReactNode;
  lastUpdated: ReactNode;
  location: string;
  id: number;
};
export const IndividualsTab = () => {
  const [searchValue, setSearchValue] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('');
  const [hasSearched, setHasSearched] = useState(false);
  const [sort, setSort] = useState<IndividualSort>({
    columnKey: 'statusUpdatedAt',
    direction: 'descending',
  });
  const [columnFilters, setColumnFilters] = useState<IndividualFilters>(emptyIndividualFilters);
  const token = useSelector((state: RootState) => state.auth.auth0AccessToken);

  const navigate = useNavigate();

  const { data: individualsData } = useGetIndividualsQuery(undefined, {
      skip: !token,
    });
  const [fetchFilters, { data: filtersData, isLoading }] = useSearchIndividualMutation();

  const [debouncedSearchValue] = useDebounce(searchValue, 500);

  const fetchFilteredData = (searchTerm: string, status: string) => {
    setHasSearched(searchTerm !== '' || status !== '');
    fetchFilters({
      searchTerm,
      status,
      pageSize: 99999999,
      pageNumber: 1,
    });
  };

  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter);
    fetchFilteredData(debouncedSearchValue, filter);
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleRowClick = (record: IndividualTableData) => {
    navigate(`/individual-details/${record.id}`);
  };

  // Options come from the records the search returned, so the panels only ever offer
  // values that are actually present.
  const filterOptions = {
    type: getFilterOptions(filtersData, individualFilterValues.type),
    assignedToUser: getFilterOptions(filtersData, individualFilterValues.assignedToUser),
  };

  const filteredData = filterByValues(
    filterByValues(
      filterByDateRange(
        filtersData,
        (record) => record.statusUpdatedAt,
        columnFilters.statusUpdatedAt
      ),
      individualFilterValues.type,
      columnFilters.type
    ),
    individualFilterValues.assignedToUser,
    columnFilters.assignedToUser
  );

  const sortedIndividualsData = sortTableData(filteredData, sort, individualSortValues);

  const columns = getIndividualColumns({
    sort,
    onSortChange: setSort,
    filters: columnFilters,
    onFiltersChange: setColumnFilters,
    filterOptions,
  });

  const updateFilters = (changes: Partial<IndividualFilters>) =>
    setColumnFilters({ ...columnFilters, ...changes });

  const lastUpdatedRange = columnFilters.statusUpdatedAt;

  const describeSelection = (selected: string[]) =>
    selected.length === 1 ? selected[0] : `${selected.length} selected`;

  const getNoDataMessage = () => {
    if (!hasSearched) {
      return 'You have no KYC verifications started. Start your first KYC verification today!';
    }
    return 'No records found';
  };

  useEffect(() => {
    if (searchValue === '') {
      fetchFilteredData('', activeFilter);
    }
  }, [searchValue]);

  useEffect(() => {
    if (searchValue !== '') return;

    if (token) {
      fetchFilteredData('', activeFilter);
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchFilteredData('', activeFilter);
    }, AUTH_TOKEN_WAIT_MS);

    return () => clearTimeout(timeoutId);
  }, [searchValue, token]);

  useEffect(() => {
    if (debouncedSearchValue !== '') {
      fetchFilteredData(debouncedSearchValue, activeFilter);
    }
  }, [debouncedSearchValue, activeFilter]);
  return (
    <div className="mb-[80px]">
      <TableHeader
        activeFilter={activeFilter}
        data={individualsData}
        handleFilterClick={handleFilterClick}
        onChange={handleSearch}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
      />

      <div className="table-filter-chips">
        {columnFilters.type.length > 0 && (
          <FilterChip
            label={`Type: ${describeSelection(columnFilters.type)}`}
            panel={
              <CheckboxFilter
                options={filterOptions.type}
                value={columnFilters.type}
                onChange={(type) => updateFilters({ type })}
              />
            }
            onClear={() => updateFilters({ type: [] })}
          />
        )}

        {columnFilters.assignedToUser.length > 0 && (
          <FilterChip
            label={`Assigned To: ${describeSelection(columnFilters.assignedToUser)}`}
            panel={
              <CheckboxFilter
                options={filterOptions.assignedToUser}
                value={columnFilters.assignedToUser}
                onChange={(assignedToUser) => updateFilters({ assignedToUser })}
              />
            }
            onClear={() => updateFilters({ assignedToUser: [] })}
          />
        )}

        {lastUpdatedRange && (
          <FilterChip
            label={`Last Updated: ${format(lastUpdatedRange[0], 'dd/MM/yyyy')} - ${format(
              lastUpdatedRange[1],
              'dd/MM/yyyy'
            )}`}
            panel={
              <DateRangeFilter
                value={lastUpdatedRange}
                onChange={(statusUpdatedAt) => updateFilters({ statusUpdatedAt })}
              />
            }
            onClear={() => updateFilters({ statusUpdatedAt: null })}
          />
        )}
      </div>

      <TableComponent
        columns={columns}
        noDataMessage={getNoDataMessage()}
        dataSource={sortedIndividualsData}
        className="entities-table"
        isLoading={isLoading}
        onRow={(record: IndividualTableData) => ({
          onClick: () => handleRowClick(record),
        })}
      />
    </div>
  );
};
